import type { BaseResponse, PaginationData } from '@/utils/request';
import { request } from '@/utils/request';

// ====== 订单相关 ======

/** 订单列表请求参数 */
export interface OrderListParams {
  page: number
  limit: number
  /** 订单阶段：0-全部, 1-待付款, 2-待发货, 3-已发货, 4-退款/售后 */
  orderStage?: number
  /** 退款状态 */
  refundStatus?: number
  /** 站点ID */
  siteId?: string | number
  /** 平台ID */
  platformId?: string
  /** 是否备注 */
  isRemark?: string
  /** 超时发货状态 */
  ptDeliverOutStatus?: string
  /** 搜索字段 */
  searchField?: string
  /** 搜索关键词 */
  searchKeyWord?: string
  /** 创建时间 */
  createTime?: string
}

/** 加载订单列表 */
export function getOrderList(data: Partial<OrderListParams>) {
  return request<PaginationData<API.OrderListItem[]>>({
    url: '/newapi/order/getOrderList',
    method: 'post',
    data,
  });
}

/** 订单数量分组项 */
export interface OrderNumGroupItem {
  /** 订单状态：1=待支付 / 2=待发货 / 3=已发货 / 5=全部退款(售后) / 0=已取消 等 */
  status: number | null
  /** 状态文本 */
  statusText: string
  /** 订单数量 */
  orderNum: number
  /** 是否在菜单上显示数量徽标 */
  isShowNum?: number
}

/** 发货/货品状态分组项 */
export interface DeliverOrderNumGroupItem {
  status: number | null
  statusText: string
  orderNum: number
}

/** 订单数量统计返回 */
export interface OrderNumData {
  /** 订单状态分组（用于待支付/待发货/已发货/售后等计数） */
  orderNumGroups: OrderNumGroupItem[]
  /** 发货/货品状态分组（用于“货品状态”区域展示） */
  deliverOrderNumGroups: DeliverOrderNumGroupItem[]
}

/** 获取订单数量统计（我的页面 badge） */
export function getOrderNum() {
  return request<OrderNumData>({
    url: '/newapi/order/getOrderNumGroup',
    method: 'post',
  });
}

/** 取消订单 */
export function cancelOrder(data: { orderId: string | number }) {
  return request<BaseResponse<any>>({
    url: '/newapi/order/cancelOrder',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '订单已取消',
  });
}

/** 钱包支付订单 */
export function payOrderByWallet(data: Record<string, any>) {
  return request<BaseResponse<any>>({
    url: '/newapi/pay/pay/payOrderByAmount',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}

/** 第三方支付订单（支付宝/微信），后端返回支付参数或跳转URL */
export function payOrderByAlipay(data: {
  order_ids: (string | number)[]
  /** 平台类型 */
  type: number
  /** 支付完成返回URL（H5用） */
  return_url?: string
}) {
  return request<any>({
    url: '/pay/orderPay',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}

/** 提前获取快递单号 */
export function getOrderCourierNo(data: { orderId: string | number }) {
  return request<BaseResponse<any>>({
    url: '/newapi/order/getShippingSn',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '订单已提前获取快递单号',
  });
}

/** 加载订单详情 */
export function getOrderDetail(data: { orderId: string | number }) {
  return request<API.OrderDetail>({
    url: '/newapi/order/getOrderDetail',
    method: 'post',
    data,
  });
}

/** 加载订单操作日志 */
export function getOrderLogs(data: { orderId: string | number }) {
  return request<API.OrderLogItem[]>({
    url: '/newapi/order/getOrderLog',
    method: 'post',
    data,
  });
}

/** 物流轨迹查询 */
export function logisticsTraceSearch(data: { mailNo: string, cpCode: string, tel?: string }) {
  return request<any>({
    url: '/newapi/logistic/express',
    method: 'post',
    data,
  });
}

/** 更新订单物流（拦截快递/修改派送地址） */
export function interceptCourier(data: {
  orderId: string | number
  destinationType: number
  password: string
  receiveAddress?: {
    shippingName: string
    mobile: string
    province: string
    provinceId: number
    city: string
    cityId: number
    district: string
    districtId: number
    address: string
  }
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/order/interceptCourier',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}

/** 修改订单收货地址 */
export function updateOrderAddress(data: {
  orderId: string | number
  receiveAddress: {
    shippingName: string
    mobile: string
    province: string
    provinceId: number
    city: string
    cityId: number
    district: string
    districtId: number
    address: string
  }
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/order/editAddress',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '修改成功！',
  });
}

/** 修改订单快递 */
export function updateOrderCourier(data: {
  orderId: string | number
  wpId: string | number
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/order/editOrderCourierInfo',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '修改完成！',
  });
}

/** 将订单改为礼品单 */
export function updateOrderToGift(data: {
  order_id: string | number
  sku_id: string | number
}) {
  return request<BaseResponse<any>>({
    url: '/v1/dsyOrder/editOrderToGift',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '改礼品单完成！',
  });
}

/** 修改订单备注 */
export function editOrderRemark(data: { orderId: [string | number], remark: string }) {
  return request({
    url: '/newapi/order/editOrderRemark',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}

/** 修改有货先发服务 */
export function editOrderIsOutStockRefund(data: { orderId: string | number, isOutStockRefund: number }) {
  return request({
    url: '/newapi/order/editOrderIsOutStockRefund',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}

// ====== 历史订单相关 ======

/** 加载历史订单列表 */
export function getHistoryOrderList(data: {
  page: number
  limit: number
  month: string
  platformId?: number
  isRemark?: number
  searchField?: number
  searchKeyWord?: string
}) {
  return request<any>({
    url: '/newapi/goods/dsyOrderHistory/getList',
    method: 'post',
    data,
  });
}

// ====== 售后/退款相关 ======

/** 加载订单售后记录 */
export function getOrderRefundRecord(data: { orderId: string | number }) {
  return request<any[]>({
    url: '/newapi/aftersales/refund/getAllRefundInfo',
    method: 'post',
    data,
  });
}

/** 加载售后详情 */
export function getRefundDetail(data: { orderId: string | number, id: string | number }) {
  return request<any>({
    url: '/newapi/aftersales/refund/getRefundInfo',
    method: 'post',
    data,
  });
}

/** 获取售后退货地址 */
export function getRefundAddress(siteId: string | number) {
  return request<any>({
    url: '/newapi/site/site/getRefundAddress',
    method: 'get',
    data: { siteId },
  });
}

/** 获取快递公司列表 */
export function getLogisticsList() {
  return request<any[]>({
    url: '/newapi/order/logistics/getLogisticsList',
    method: 'get',
    data: {},
  });
}

/** 更新售后快递信息 */
export function updateRefundCourier(data: {
  orderId: string | number
  id: string | number
  courierId: string | number
  courierName: string
  courierCode: string
  courierNo: string
  content?: string
}) {
  return request<any>({
    url: '/newapi/aftersales/refund/editRefund',
    method: 'post',
    data,
  });
}

/** 取消售后申请 */
export function cancelRefund(data: { orderId: string | number, id: string | number }) {
  return request<any>({
    url: '/newapi/aftersales/refund/cancelRefund',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '撤销成功',
  });
}

/** 计算申请售后金额 */
export function calculateRefundAmount(data: {
  orderId: string | number
  refundType: number
  refundItems: { orderItemId: number, refundNum: number, isApplyQuality: number }[]
}) {
  return request<API.CalculateRefundAmount>({
    url: '/newapi/aftersales/refund/calculateRefundAmount',
    method: 'post',
    data,
  });
}

/** 申请售后 */
export function applyRefund(data: {
  orderId: string | number
  refundType: number
  content: string
  applyRefundMoney: number
  courierId?: string
  courierName?: string
  courierNo?: string
  courierCode?: string
  isRefundCheckGoods?: number
  refundItems: { orderItemId: number, refundNum: number, isApplyQuality: number }[]
}) {
  return request<any>({
    url: '/newapi/aftersales/refund/applyRefund',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '申请成功',
  });
}

// ====== 退货/售后相关 ======

/** 退货列表请求参数 */
export interface RefundOrderListParams {
  page: number
  limit: number
  /** 站点ID */
  siteId?: number | string
  /** 退款类型 */
  refundType?: string
  /** 退款状态 */
  refundStatus?: string
  /** 快递类型 */
  courierType?: string
  /** 订单备注: 1-有备注, 0-无备注 */
  isOrderRemark?: string
  /** 退货商品类型: 0-未知, 1-部分退款, 2-全部退款 */
  refundGoodsType?: string
  /** 订单号 */
  orderId?: string
  /** 快递单号 */
  courierNo?: string
  /** 创建时间范围 */
  createTime?: string
}

/** 加载退货列表（售后记录列表） */
export function getRefundOrderList(data: Partial<RefundOrderListParams>) {
  return request<PaginationData<API.RefundOrderItem[]>>({
    url: '/newapi/aftersales/refund/getRefundList',
    method: 'post',
    data,
  });
}

/** 获取平台订单退货信息 */
export function getPlatformOrderRefundInfo(data: {
  ptId: number | string
  shopId: string
  ptOrderSn: string
}) {
  return request<BaseResponse<API.PlatformRefundData>>({
    url: '/newapi/pt/ptRefund/getInfo',
    method: 'post',
    data,
  });
}

/** 处理平台订单退货 */
export function handlePlatformOrderRefund(data: {
  operateType: number
  orderId: string | number
  id: number | string
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/refund/agreePlatformRefund',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}

/** 读取消息 */
export function readNotice(data: {
  id_list: (number | string)[]
  type: string
}) {
  return request<BaseResponse<any>>({
    url: '/common/setRead',
    method: 'post',
    data,
  });
}

// ====== 理赔相关 ======

/** 理赔列表请求参数 */
export interface RedressListParams {
  page: number
  limit: number
  /** 站点ID */
  siteId?: number | string
  /** 检查服务等级: 1-初级检查, 2-高级检查 */
  serviceGrade?: string
  /** 服务状态: 0-待审核, 1-审核挂起, 2-审核成功, 3-审核拒绝 */
  serviceStatus?: string
  /** 服务类型: 0-其他, 1-尺码错误, 2-颜色错, 3-货号错, 4-款式错 */
  serviceType?: string
  /** 订单号 */
  orderId?: string
  /** 创建时间范围 */
  createTime?: string
}

/** 加载理赔列表 */
export function getRedressList(data: Partial<RedressListParams>) {
  return request<PaginationData<API.RedressOrderItem[]>>({
    url: '/newapi/aftersales/orderQualityService/getList',
    method: 'post',
    data,
  });
}

/** 加载理赔详情 */
export function getRedressDetail(data: { id: number | string }) {
  return request<API.RedressOrderDetail>({
    url: '/newapi/aftersales/orderQualityService/getInfo',
    method: 'post',
    data,
  });
}

// ====== 售后任务相关 ======

/** 加载售后任务列表 */
export function getRefundTaskList(data: {
  page: number
  limit: number
  siteId?: number | string
  commitType?: string
  dealStatus?: string
  taskType?: string
  dealType?: string
  createTime?: string
}) {
  return request<PaginationData<any[]>>({
    url: '/newapi/aftersales/task/getList',
    method: 'GET',
    data,
  }, {
    cache: false,
  });
}

/** 加载售后任务详情 */
export function getRefundTaskDetail(data: { id: number | string }) {
  return request<any>({
    url: '/newapi/aftersales/task/getInfo',
    method: 'GET',
    data,
  });
}

/** 申请售后任务 */
export function applyRefundTask(data: {
  taskType: number
  courierNo: string
  taskDesc: string
  siteId: string
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/task/addTask',
    method: 'POST',
    data,
  }, {
    notificationType: 'all',
    successMsg: '申请成功',
  });
}

/** 完成售后任务 */
export function confirmRefundTask(data: { id: number | string }) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/task/completeTask',
    method: 'POST',
    data,
  }, {
    notificationType: 'all',
    successMsg: '售后已完成',
  });
}

/** 取消售后任务 */
export function cancelRefundTask(data: { id: number | string }) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/task/deleteTask',
    method: 'POST',
    data,
  }, {
    notificationType: 'all',
    successMsg: '取消成功',
  });
}

/** 售后任务详情 添加回复 */
export function addReplyRefundTask(data: {
  id: number | string
  content: string
  imageList?: string[]
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/task/addLog',
    method: 'POST',
    data,
  }, {
    notificationType: 'all',
    successMsg: '回复成功',
  });
}

/** 设置售后任务的订单号 */
export function editRefundTaskOrderId(data: {
  id: number | string
  orderId: string
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/task/setOrderId',
    method: 'POST',
    data,
  }, {
    notificationType: 'all',
    successMsg: '设置成功',
  });
}

// ====== 售后通知角标数据 ======

/** 售后通知数量接口返回（newapi/dsydatastatistics/getAfterSalesStatistics） */
export interface ReturnTjData {
  /** 缺货订单明细数（缺货统计） */
  lackOrderDetailNum?: number
  /** 未读订单退款数（退货列表） */
  unreadOrderRefundNum?: number
  /** 未读退款任务数（售后任务） */
  unreadRefundTaskNum?: number
  /** 未读退货订单数（异常/退件寄回） */
  unreadReturnOrderNum?: number
  /** 未读订单质量数（订单理赔） */
  unreadOrderQualityNum?: number
}

/** 获取售后通知数量（我的页面角标） */
export function getReturnTj() {
  return request<ReturnTjData>({
    url: '/newapi/dsydatastatistics/getAfterSalesStatistics',
    method: 'GET',
  });
}

// ====== 缺货统计相关 ======

/** 加载缺货统计列表（按档口分组） */
export function getLackGoodsList(data?: Record<string, any>) {
  return request<API.LackGoodsGroup[]>({
    url: '/newapi/order/getLackOrderGroup',
    method: 'post',
    data: { ...data },
  });
}

/** 加载缺货SKU对应的缺货订单列表 */
export function getLackGoodsOrderList(data: {
  skuId: string | number
  page?: number
  limit?: number
}) {
  return request<PaginationData<API.LackOrderItem[]>>({
    url: '/newapi/order/getLackOrderList',
    method: 'post',
    data: { page: 1, limit: 200, ...data },
  });
}

// ====== 退件寄回相关 ======

/** 退件寄回列表请求参数 */
export interface ReturnSendBackListParams {
  page: number
  limit: number
  /** 站点ID */
  siteId?: number | string
  /** 退回方式: 1-快递寄回, 2-自提, 3-销毁 */
  courierType?: string
  /** 订单状态: 0-未揽收, 1-已揽收 */
  orderStatus?: string
  /** 商品类型: 1-异常件, 2-退货件 */
  goodsType?: string
  /** 异常快递单号 */
  refundRecordNo?: string
  /** 寄回快递单号 */
  courierNo?: string
}

/** 加载退件寄回列表 */
export function getReturnSendBackList(data: Partial<ReturnSendBackListParams>) {
  return request<PaginationData<API.ReturnSendBackItem[]>>({
    url: '/newapi/aftersales/courierReturnOrder/getList',
    method: 'get',
    data,
  });
}

/** 检测退件包裹快递单号 */
export function checkReturnCourierNo(data: { courierNo: string }) {
  return request<API.ReturnSendBackCheckData>({
    url: '/newapi/aftersales/courierReturnOrder/checkCourierNo',
    method: 'get',
    data,
  });
}

/** 新增退件寄回申请 */
export function addReturnSendBack(data: Record<string, any>, siteCode: string) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/courierReturnOrder/apply',
    method: 'post',
    data,
    headers: {
      site: siteCode,
    },
  }, {
    notificationType: 'all',
    successMsg: '申请成功',
  });
}

/** 修改退件寄回申请 */
export function updateReturnSendBack(data: Record<string, any>) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/courierReturnOrder/edit',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '修改成功',
  });
}

/** 计算退件寄回费用 */
export function calculateSendBackAmount(data: Record<string, any>, siteCode: string) {
  return request<{ paymentMoney: number, servicePrice: number, courierPrice: number, rentPrice: number }>({
    url: '/newapi/aftersales/courierReturnOrder/calculateAmount',
    method: 'post',
    data,
    headers: {
      site: siteCode,
    },
  });
}

/** 取消退件寄回申请 */
export function cancelReturnSendBack(data: { id: number | string }) {
  return request<BaseResponse<any>>({
    url: '/newapi/aftersales/courierReturnOrder/cancel',
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '取消成功',
  });
}

/** 更新订单商品（换货/改款）；silent 为 true 时不弹提示（用于批量改款逐单提交） */
export function updateOrderGoods(data: {
  orderId: string | number
  detailId: string | number
  goodsId: string | number
  skuId: string | number
}, silent = false) {
  return request<BaseResponse<any>>({
    url: '/newapi/order/editOrderDetailGoods',
    method: 'post',
    data,
  }, silent
    ? { notificationType: 'none' }
    : { notificationType: 'all', successMsg: '改款完成！' });
}

/**
 * 查找可用于换商品的商品列表
 * type=0 按条件（店铺+货号），type=1 以图搜款
 */
export function getOrderGoodsBindList(
  type: number,
  data: Record<string, any>,
  siteCode: string,
) {
  let url = '';
  if (type === 1) {
    // 按图片搜图
    url = '/newapi/goods/goods/goodsImgGetGlData';
  }
  else if (type === 2) {
    // 按绑定历史
    url = '/newapi/platform/ptGoods/getSkuGlInfo';
  }
  else if (type === 3) {
    // 按商品sku搜索（仅‘拼多多’）
    url = '/newapi/platform/ptGoods/outCodeGetGlData';
  }
  else if ([0, 4, 5, 6, 7, 13].includes(type)) {
    // 按礼品类型查询, 按商品id, 按增值服务类型查询，条件搜索 （档口货号），云仓闪发（云档口商品），库存列表
    url = '/newapi/goods/goods/getGoodsList';
  }
  return request<any>({
    url,
    method: 'post',
    data,
    headers: {
      site: siteCode,
    },
  });
}

// 代发车列表
export function getCartList(data: {
  limit: number
  page: number
  sellerId?: number
  siteId?: number | string
}) {
  return request<PaginationData<API.GetCartListResult[]>>({
    url: `/newapi/order/carts/getCartList`,
    method: 'post',
    data,
  });
}

// 代发车添加对应sku
export function addCartSkuNew(data: {
  goodsId: number
  skus: { count: number, skuId: number }[]
}, siteCode: string) {
  return request<any>(
    {
      url: `/newapi/order/carts/addToCart`,
      method: 'post',
      data,
      headers: {
        site: siteCode,
      },
    },
    {
      notificationType: 'all',
    },
  );
}

// 删除代发车中对应的sku
export function delCartSkuNew(data: {
  cartIds: number[]
  goodsId?: number
  sellerId: string
}) {
  return request<any>({
    url: `/newapi/order/carts/delFromCart`,
    method: 'post',
    data,
  });
}

// 修改代发车中对应的sku
export function editCartSkuNew(data: {
  cartId: number
  count: number
}) {
  return request<any>({
    url: `/newapi/order/carts/editCart`,
    method: 'post',
    data,
  });
}

/** 获取订单通知数量 */
export function getOrderNoticeList(sellerId?: number) {
  const data: Record<string, any> = {
    checkStatus: 0,
  };
  if (sellerId) {
    data.sellerId = sellerId;
  }
  return request<{ total: number }>({
    url: '/newapi/user/orderNotice/getOrderNoticeList',
    method: 'POST',
    data,
  });
}

/** 订单通知列表参数 */
export interface OrderNoticeListParams {
  page: number
  limit: number
  notice_type?: number | string
  check_status?: number | string
  sellerId?: number
}

/** 加载订单通知列表 */
export function loadOrderNoticeList(data: OrderNoticeListParams) {
  return request<PaginationData<API.OrderNoticeItem[]>>({
    url: '/newapi/user/orderNotice/getOrderNoticeList',
    method: 'POST',
    data,
  });
}

/** 确认订单通知（标记处理状态） */
export function confirmOrderNotice(data: {
  ids: number[]
  checkStatus: number
}) {
  return request<BaseResponse<any>>({
    url: '/newapi/user/orderNotice/setOrderNoticeCheckStatus',
    method: 'POST',
    data,
  });
}
// ====== 手工下单相关 ======

/** 获取用户下单配置 */
export function getUserOrderConfig() {
  return request<API.UserOrderConfig>({
    url: '/newapi/user/configs/getConfig',
    method: 'GET',
    data: {},
  });
}

/** 设置用户下单配置 */
export function setUserOrderConfig(data: API.SetUserOrderConfigParams) {
  return request({
    url: '/newapi/user/configs/setConfig',
    method: 'POST',
    data,
  }, {
    notificationType: 'all',
    isGetDataDirectly: false,
  });
}

/**
 * 获取站点快递列表
 * deliverType: 交付类型 0：普通代发 1：礼品代发 2：普通+礼品 11：云仓 12：云仓闪发
 * orderType: 订单类型 1：通用 2：关联单 3：手工单 4：线下单
 * isForReturnOrder: 是否用于寄回
 */
export function getSiteCourierList(data: {
  deliverType?: number
  isForReturnOrder?: 0 | 1
  orderType?: number
} = {}) {
  return request<{ [key: string]: API.SiteCourier }>({
    url: '/newapi/order/logistics/getCourierList',
    method: 'GET',
    data,
  });
}

/**
 * 查询检查服务列表
 */
export function getQualityCheckServiceList() {
  return request<API.QcServiceItem[]>({
    url: `/newapi/order/service/getQualityCheckServiceList`,
    method: 'get',
  });
}

/** 获取增值服务列表 */
export function getAddedServices() {
  return request<API.SiteGifGoods[]>({
    url: '/newapi/order/getLpGoodsListSite',
    method: 'POST',
    data: { goodsType: 3 },
  });
}

/** 根据订单地址获取快递价格 */
export function getCourierPriceByArea(data: { list: any[] }) {
  return request<API.CourierPriceInfo[]>({
    url: '/newapi/order/logistics/getCourierPriceList',
    method: 'POST',
    data,
  });
}

/** 获取平台订单密文地址（抖音下单用） */
export function getPlatformOrderAddress(data: {
  platformId: number
  platformOrderId: string
}) {
  return request<any>({
    url: '/newapi/platform/order/getOrderAddressInfo',
    method: 'POST',
    data,
  });
}

/** 生成订单 */
export function createOrderByPlatform(data: Record<string, any>) {
  return request<{ orderId: string | number, paymentMoney: number }>({
    url: '/newapi/order/createOrderByPlatform',
    method: 'POST',
    data,
  });
}

/** 加载优惠券列表 */
export function getOrderCouponList(params?: Record<string, any>) {
  return request<any>({
    url: '/newapi/order/coupon/getCouponList',
    method: 'POST',
    data: params || { isCanUse: 1, limit: 999 },
  });
}
